/** Shared Result API transport. The deadline includes body reading and retries. */
import type { NitroFetchRequest } from "nitropack";

const DEFAULT_REQUEST_TIMEOUT = 20_000;
type FailureKind = "network" | "timeout" | "abort";

const transportFailure = (kind: FailureKind, cause?: unknown) => {
  const errorMessage =
    kind === "timeout"
      ? "The request timed out. Please try again."
      : kind === "abort"
        ? "The request was cancelled."
        : "Network error, please try again later";

  return Object.assign(new Error(errorMessage), {
    name: kind === "timeout" ? "TimeoutError" : kind === "abort" ? "AbortError" : "RequestError",
    kind,
    status: 0,
    _data: { code: -1, message: errorMessage, success: false },
    cause,
  });
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

type ResponseWithData = { status: number; _data?: unknown };
const isResponse = (value: unknown): value is ResponseWithData => isRecord(value) && typeof value.status === "number";

const responseFailure = <T extends ResponseWithData>(response: T) => {
  const data = response._data;
  if (!isRecord(data) || (typeof data.code !== "number" && typeof data.code !== "string")) {
    response._data = {
      ...(isRecord(data) ? data : {}),
      code: response.status >= 400 ? response.status : -1,
      message: "Unable to read the server response. Please try again.",
      success: false,
    };
  }
  return response;
};

const apiRequest = async <T>(url: NitroFetchRequest, options: any): Promise<ResultData<T>> => {
  const config = useRuntimeConfig();
  const nuxtApp = useNuxtApp();
  const { contentType = "application/json", timeout, signal, onRequest, ...fetchOptions } = options;
  const timeoutMs =
    typeof timeout === "number" && Number.isFinite(timeout) && timeout > 0 ? timeout : DEFAULT_REQUEST_TIMEOUT;
  const controller = new AbortController();
  let interruptedError: ReturnType<typeof transportFailure> | undefined;
  let rejectInterrupted!: (error: unknown) => void;
  const interrupted = new Promise<never>((_resolve, reject) => {
    rejectInterrupted = reject;
  });
  const interrupt = (kind: FailureKind, cause?: unknown) => {
    if (interruptedError) return;
    interruptedError = transportFailure(kind, cause);
    controller.abort(interruptedError);
    rejectInterrupted(interruptedError);
  };
  const onCallerAbort = () => interrupt("abort", signal?.reason);
  const timer = setTimeout(() => interrupt("timeout"), timeoutMs);
  signal?.addEventListener("abort", onCallerAbort, { once: true });

  try {
    if (signal?.aborted) onCallerAbort();
    const response = await Promise.race([
      $fetch.raw<ResultData<T>>(url, {
        baseURL: config.public.baseURL,
        ...fetchOptions,
        // The controller below covers the entire operation; ofetch's timeout only
        // covers waiting for response headers in the currently installed version.
        timeout: 0,
        signal: controller.signal,
        async onRequest(context) {
          if (controller.signal.aborted) throw interruptedError;
          const token = import.meta.client ? localStorage.getItem("token") || "" : "";
          const headers = new Headers({
            "Content-Type": contentType,
            Cookies: `token=${token}`,
            "X-Tenant-Id": String(config.public.tenant_id),
            "X-Access-Token": token,
          });
          new Headers(context.options.headers).forEach((value, key) => headers.set(key, value));
          context.options.headers = headers;
          for (const hook of Array.isArray(onRequest) ? onRequest : onRequest ? [onRequest] : []) {
            await hook(context);
          }
          // A hook or retry delay may finish after the total deadline.
          if (controller.signal.aborted) throw interruptedError;
          context.options.signal = controller.signal;
        },
      }),
      interrupted,
    ]);

    const data = response._data;
    if (response.status >= 200 && response.status < 300 && isRecord(data)) {
      if (data.code === 200) return data as ResultData<T>;
      if (data.code === 404) {
        if (import.meta.client) message.error("The product does not exist.");
        // Detail views rely on this existing top-level business-code contract.
        throw data;
      }
    }
    throw responseFailure(response);
  } catch (error: unknown) {
    const failure =
      interruptedError ||
      (isRecord(error) && isResponse(error.response)
        ? responseFailure(error.response)
        : isResponse(error)
          ? responseFailure(error)
          : isRecord(error) && error.code === 404
            ? error
            : transportFailure("network", error));

    if (!import.meta.client && !("kind" in failure && failure.kind === "abort")) {
      // Preserve the non-client error route without swallowing the rejected request.
      const data = "_data" in failure && isRecord(failure._data) ? failure._data : failure;
      try {
        // Navigation must not extend the request's total deadline.
        void Promise.resolve(
          nuxtApp.runWithContext(() =>
            navigateTo({
              path: "/Error",
              query: {
                code: "code" in data ? String(data.code) : "-1",
                message: "msg" in data ? String(data.msg) : "message" in data ? String(data.message) : "Request failed",
              },
            }),
          ),
        ).catch(() => {});
      } catch {
        // Navigation failure must not replace the original request failure.
      }
    }
    throw failure;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onCallerAbort);
  }
};

interface Result {
  code: number;
  message: string;
  success: boolean;
}

interface ResultData<T = any> extends Result {
  result: T;
}

export const getApi = <T>(url: NitroFetchRequest, options: any = {}): Promise<ResultData<T>> => {
  return apiRequest(url, {
    method: "GET",
    ...options,
  });
};

export const postApi = <T>(url: NitroFetchRequest, options: any = {}): Promise<ResultData<T>> => {
  return apiRequest(url, {
    method: "POST",
    ...options,
  });
};

export const deleteApi = <T>(url: NitroFetchRequest, options: any = {}): Promise<ResultData<T>> => {
  return apiRequest(url, {
    method: "DELETE",
    ...options,
  });
};

const Errcode = Object.freeze({
  RecordNotFound: 90001,
});

export { Errcode };
